/* ===================== EditProfile.js ===================== */
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Divider,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { investorApi } from "../../api/api";
import {
  loginUser,
  logoutUser,
  loginUser as loginUserAction,
} from "../../redux/reducers/userReducer";
import LogoutIcon from "@mui/icons-material/Logout";
import SecondaryButton from "../common/SecondaryButton";
import GradientButton from "../common/GradiantButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";

const EditProfile = () => {
  const { state } = useLocation();
  const userData = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);

  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ================= INIT ================= */
  useEffect(() => {
    if (userData) {
      setName(`${userData.firstName || ""} ${userData.lastName || ""}`.trim());
      setEmail(userData.Email || "");
      setMobile(userData.Mobilenumber || "");
    }
  }, [userData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ================= RESEND TIMER ================= */
  useEffect(() => {
    if (resendTimer === 0) return;

    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  const minutes = Math.floor(resendTimer / 60);
  const seconds = resendTimer % 60;

  /* ================= LOGOUT ================= */
  const handleLogout = () => {
    localStorage.removeItem("token");
    dispatch(logoutUser());
    navigate("/", { replace: true });
  };

  /* ================= SAVE PROFILE ================= */
  const handleSave = async () => {
    const [firstName, ...rest] = name.trim().split(" ");
    const lastName = rest.join(" ");

    try {
      const res = await investorApi.updateProfile(userData.userId, {
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Mobilenumber: mobile,
      });

      if (res.status === 200) {
        dispatch(
          loginUser({
            user: {
              id: userData.userId,
              firstName,
              lastName,
              avatar: userData.avatar,
              role: userData.role,
              wallet: userData.wallet,
              Email: email,
              Mobilenumber: mobile,
              isSubscribed: userData.isSubscribed,
              isKyc: userData.isKyc,
              refferal_code: userData.user_refferal_code,
              isVerified: userData.isVerified,
            },
          })
        );

        setSnackbar({
          open: true,
          message:
            email !== userData.Email
              ? "Profile updated. Please verify your email."
              : "Profile updated successfully",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update profile",
        severity: "error",
      });
    }
  };

  /* ================= SEND EMAIL OTP ================= */
  const handleEmailVerify = async () => {
    if (isSendingOtp || resendTimer > 0) return;

    try {
      setIsSendingOtp(true);

      const response = await investorApi.SendEmailOtp({ email });

      if (response?.data?.success) {
        setSnackbar({
          open: true,
          message: "OTP sent to your email. Please check your inbox.",
          severity: "success",
        });

        setShowOtp(true);
        setResendTimer(240); // ⏱ 4 minutes
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to send verification email",
        severity: "error",
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  /* ================= VERIFY OTP ================= */
  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");

    try {
      await investorApi.verifyEmail({ email, otp: enteredOtp });

      dispatch(
        loginUserAction({
          user: {
           id: userData.userId,
              firstName: userData.firstName,
              lastName: userData.lastName,
              avatar: userData.avatar,
              role: userData.role,
              wallet: userData.wallet,
              Email: email,
              Mobilenumber: mobile,
              isSubscribed: userData.isSubscribed,
              isKyc: userData.isKyc,
              refferal_code: userData.user_refferal_code,
            isVerified: true,
          },
        })
      );

      setSnackbar({
        open: true,
        message: "Email verified successfully! Please set your password.",
        severity: "success",
      });

      setShowOtp(false);

      navigate("/myprofile/reset-password", {
        state: { email },
        replace: true,
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Invalid OTP",
        severity: "error",
      });
    }
  };

  /* ================= OTP INPUT ================= */
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    if (!value && index > 0) otpRefs.current[index - 1]?.focus();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      {/* HEADER */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          Edit Your Profile
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* NAME */}
      <Typography fontWeight={500}>Name</Typography>
      <TextField fullWidth value={name} onChange={(e) => setName(e.target.value)} />

      {/* EMAIL */}
      <Typography sx={{ mt: 3 }} fontWeight={500}>
        Email
      </Typography>
      <TextField fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
        {!userData?.isVerified && ( <Typography sx={{ mt: 1, mb: 2, fontSize: 13, color: "#d32f2f", fontWeight: 500 }} > ⚠️ Email verification is mandatory to post a property. </Typography> )}

      {!userData?.isVerified && (
        <Typography
          sx={{
            mt: 1,
            mb: 2,
            fontSize: 13,
            color: resendTimer > 0 ? "gray" : "#1976d2",
            cursor: resendTimer > 0 ? "not-allowed" : "pointer",
          }}
          onClick={handleEmailVerify}
        >
          {resendTimer > 0
            ? `Resend OTP in ${minutes}:${seconds
              .toString()
              .padStart(2, "0")}`
            : "Click here to generate email verification mail."}
        </Typography>
      )}

      {/* OTP */}
      {showOtp && (
        <Box sx={{ width: "60%" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {otp.map((digit, i) => (
              <TextField
                key={i}
                value={digit}
                inputRef={(el) => (otpRefs.current[i] = el)}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                inputProps={{ maxLength: 1, style: { textAlign: "center" } }}
              />
            ))}
          </Box>

          <Button sx={{ mt: 2 }} variant="contained" onClick={handleVerifyOtp}>
            Verify OTP
          </Button>
        </Box>
      )}

      {userData?.isVerified && (<Typography sx={{ mb: 3, fontSize: { xs: 11, sm: 13 }, color: "#1976d2", cursor: "pointer", transition: "all 0.2s ease", "&:hover": { textDecoration: "underline", }, }} onClick={() => navigate("reset-password", { state: { email: userData?.Email } })} > Click here to set / reset password. </Typography>)}

      {/* MOBILE */}
      <Typography sx={{ mt: 3 }} fontWeight={500}>
        Mobile Number
      </Typography>
      <TextField fullWidth value={mobile} onChange={(e) => setMobile(e.target.value)} />

      <Box sx={{ mt: 4, textAlign: "center" }}>
        <GradientButton text="Save Profile" onClick={handleSave} />
      </Box>

      {isMobile && (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <SecondaryButton
            startIcon={<LogoutIcon />}
            text="Sign Out"
            onClick={handleLogout}
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default EditProfile;